import { create } from "zustand";
import { newsfeedApi } from "../api/newsFeedApi";
import { userDashboardAPI } from "../api/userDashboardAPI";
import { showSuccessToast } from "../js/utils/toast";
import { showErrorToast } from "../js/utils/errors";
import useUserStore from "./UseUserStore";
import { SESSION_ID } from "../api/apiConfig";
import {
	detectPostType,
	getPostId as getPostID,
	getPostUniqueId,
	getPostTypeString,
	findPostById,
} from "../utils/postTypeUtils";

const postPageSize = 10;
const commentPageSize = 5;

const useNewsfeed = create((set, get) => ({
	posts: [],
	comments: {}, // Comments organized into lists by post ID
	pinnedComments: {}, // Pinned comments organized into lists by post ID
	isCommenting: [], // Posts ids with comments open

	likedItems: [], // Items that the user has liked
	likedComments: [], // Comments that the user has liked
	processingLikes: [], // Items currently being processed for likes

	hasMorePosts: true,
	hasMoreComments: {}, // Organized by post ID

	postPageNum: 1,
	commentPageNum: 1,

	loadingPosts: false,
	loadingComments: false,

	isPosting: false,
	totalItems: 0,

	selectedPost: null, // Currently selected post for single post view

	// My Posts state
	myPosts: [],
	myPostsPageNum: 1,
	hasMoreMyPosts: true,
	loadingMyPosts: false,

	// My Salutes state
	myStarSalutes: [],
	myStarSalutesPageNum: 1,
	myStarSalutesLoadedPages: [], // Track which pages have been loaded
	hasMoreMyStarSalutes: true,
	loadingMyStarSalutes: false,

	mySecretSalutes: [],
	mySecretSalutesPageNum: 1,
	mySecretSalutesLoadedPages: [], // Track which pages have been loaded
	hasMoreMySecretSalutes: true,
	loadingMySecretSalutes: false,

	// Get all posts for page number
	getPosts: async (pageNum, filters) => {
		const state = get();
		const userState = useUserStore.getState();
		if (state.loadingPosts) {
			return;
		}

		try {
			set({ loadingPosts: true });
			const response = await newsfeedApi.getPostItems(
				pageNum,
				postPageSize,
				userState.user.MemberID ? userState.user.MemberID : 0,
				filters
			);

			// If posts found
			if (response.result?.items) {
				const newItems = response.result.items;

				// Update liked items based on server data
				const likedItems = [...state.likedItems];
				newItems.forEach((item) => {
					if (item.IsLiked) {
						// Get the correct ID based on post type
						const itemId = getPostID(item);
						if (itemId && !likedItems.includes(itemId)) {
							likedItems.push(itemId);
						}
					}
				});
				set({ likedItems, totalItems: response.result.total });

				if (pageNum === 1) {
					// Replace items for first page
					set({ posts: newItems });
				} else {
					// Append for subsequent pages, keeping only the most recent 20 items
					set({ posts: [...state.posts, ...newItems] });
				}

				set({
					hasMorePosts: response.result.hasMore,
					postPageNum: pageNum,
				});
			} else {
				set({ hasMorePosts: false });
			}
			set({ loadingPosts: false });
		} catch (error) {
			set({ loadingPosts: false });
			console.error("Error loading news items:", error);
			set({ hasMorePosts: false });
			showErrorToast(
				"Failed to load posts",
				error.response?.data?.detail ||
					error.message ||
					"Unknown error occurred"
			);
		}
	},

	// Open comment section for post with itemId by adding to isCommenting array
	onComment: (itemId) => {
		const state = get();
		const newIsCommenting = [...state.isCommenting];
		if (newIsCommenting.includes(itemId)) {
			newIsCommenting.splice(newIsCommenting.indexOf(itemId), 1);
		} else {
			newIsCommenting.push(itemId);
		}
		set({ isCommenting: newIsCommenting });
		state.loadComments(itemId);
	},

	// Handle like for post with itemId
	handleLike: async (itemId) => {
		const userState = useUserStore.getState();
		const state = get();
		if (state.loadingPosts) {
			return;
		}

		// Prevent rapid clicking by checking if we're already processing this like
		if (state.processingLikes?.includes(itemId)) {
			console.log("Already processing like for item:", itemId);
			return;
		}

		// Add to processing list
		set((state) => ({
			...state,
			processingLikes: [...(state.processingLikes || []), itemId],
		}));

		const { likedItems } = state;
		const likeIndex = likedItems.indexOf(itemId);

		try {
			// Find the post - it could have GlowID, GratitudeID, or PrideID
			let post = state.posts.find((item) => item.GlowID === itemId);
			if (!post) {
				post = state.posts.find((item) => item.GratitudeID === itemId);
			}
			if (!post) {
				post = state.posts.find((item) => item.PrideID === itemId);
			}

			const postType = detectPostType(post);

			// Optimistic UI update
			const newLikedItems = [...likedItems];
			if (likeIndex !== -1) {
				newLikedItems.splice(likeIndex, 1);
			} else {
				newLikedItems.push(itemId);
			}
			set({ likedItems: newLikedItems });

			// Send request to server
			const response = await newsfeedApi.likeGlow(
				itemId,
				userState.user.MemberID,
				postType
			);

			if (!response.data || !response.data.success) {
				// Revert liked items if server request fails
				const revertedLikedItems = [...likedItems];
				const revertedLikeIndex = revertedLikedItems.indexOf(itemId);
				if (revertedLikeIndex !== -1) {
					revertedLikedItems.splice(revertedLikeIndex, 1);
				} else {
					revertedLikedItems.push(itemId);
				}
				set({ likedItems: revertedLikedItems });

				showErrorToast(
					"Failed to update like",
					response.data?.error || "Unknown error occurred"
				);
			} else {
				// Update the item's like count and isLiked status on success
				set((state) => ({
					...state,
					posts: state.posts.map((item) => {
						// Check all possible ID fields to match the post
						if (
							item.GlowID === itemId ||
							item.GratitudeID === itemId ||
							item.PrideID === itemId
						) {
							return {
								...item,
								LikesCount:
									likeIndex !== -1 ? item.LikesCount - 1 : item.LikesCount + 1,
								IsLiked: likeIndex !== -1,
							};
						}
						return item;
					}),
				}));
				showSuccessToast(likeIndex !== -1 ? "Post unliked" : "Post liked!");
			}
		} catch (error) {
			console.error("Error updating like status:", error);
			// Revert optimistic updates on error
			const revertedLikedItems = [...likedItems];
			const revertedLikeIndex = revertedLikedItems.indexOf(itemId);
			if (revertedLikeIndex !== -1) {
				revertedLikedItems.splice(revertedLikeIndex, 1);
			} else {
				revertedLikedItems.push(itemId);
			}
			set({ likedItems: revertedLikedItems });

			showErrorToast(
				"Failed to update like",
				error.response?.data?.detail ||
					error.message ||
					"Unknown error occurred"
			);
		} finally {
			// Remove from processing list
			set((state) => ({
				...state,
				processingLikes: (state.processingLikes || []).filter(
					(id) => id !== itemId
				),
			}));
		}
	},

	// Load comments of page number for post with itemId
	loadComments: async (itemId, pageNum = 1) => {
		const state = get();
		const userState = useUserStore.getState();
		try {
			set({ loadingComments: true });

			// Find the post to get its type
			let post = state.posts.find((item) => item.GlowID === itemId);
			if (!post) {
				post = state.posts.find((item) => item.GratitudeID === itemId);
			}
			if (!post) {
				post = state.posts.find((item) => item.PrideID === itemId);
			}

			const postType = detectPostType(post);
			console.log("💬 Loading comments for post:", { itemId, postType, post });

			const response = await newsfeedApi.getComments(
				itemId,
				userState.user.MemberID,
				pageNum,
				5, // pageSize
				postType
			);

			// If comments found
			if (response.result) {
				// Update hasMore for this specific itemå
				set({
					hasMoreComments: {
						...state.hasMoreComments,
						[itemId]: response.result.hasMore,
					},
				});

				// Update comments state
				if (state.comments[itemId] && pageNum > 1) {
					// Append new comments for subsequent pages
					set({
						comments: {
							...state.comments,
							[itemId]: [
								...state.comments[itemId],
								...response.result.comments,
							],
						},
					});
				} else {
					// Update comments and comments page number state
					set((state) => ({
						...state,
						comments: {
							...state.comments,
							[itemId]: response.result.comments,
						},
						commentPages: {
							...state.commentPages,
							[itemId]: pageNum,
						},
					}));
				}

				// Track pinned comments
				if (response.result.comments) {
					const newPinnedComments = { ...state.pinnedComments };

					response.result.comments.forEach((comment) => {
						if (comment.IsPinned) {
							newPinnedComments[itemId] = comment;
							newPinnedComments[itemId].IsPinned = true;
						}
					});
					set({ pinnedComments: newPinnedComments });
				}
			}
			set({ loadingComments: false });
		} catch (error) {
			console.error("Error loading comments:", error);
			set({ loadingComments: false });
			showErrorToast(
				"Failed to load comments",
				error.response?.data?.detail ||
					error.message ||
					"Unknown error occurred"
			);
		}
	},

	// Creates new post
	handleSubmitPost: async (
		title,
		content,
		mediaFile,
		mediaType,
		recordingDuration = 0,
		contestid = null,
		questID = 1,
		memberMilestoneID = 0,
		postType = "glow",
		successToastShown = true,
		prideType = null,
		creatureID = 0,
		submissionTag = null
	) => {
		const userState = useUserStore.getState();
		try {
			set({ isPosting: true });

			// Create form data for post with potential media
			const formData = new FormData();
			formData.append("Title", title);
			formData.append("content", content);
			formData.append("MemberID", userState.user.MemberID);
			formData.append("CreatedAt", new Date().toISOString());
			formData.append("ZipCode", userState.user.Zipcode || "");
			formData.append("PartyID", userState.user.selectedContestID || "");

			// Always append ContestID when provided (including 10 for lifecorp) so backend saves with correct contest
			if (contestid !== null && contestid !== "") {
				formData.append("ContestID", String(contestid));
			}

			// Add questID to form data
			formData.append("QuestID", questID);
			formData.append("MemberMilestoneID", memberMilestoneID);

			formData.append("sessionid", SESSION_ID());

			// Determine post type - keep original type for grateful/pride, default to "glow" for contest submissions
			const typeValue =
				postType === "grateful" || postType === "pride"
					? postType
					: contestid
						? "glow"
						: postType;
			console.log("📝 Post type for submission:", {
				postType,
				contestid,
				typeValue,
			});
			formData.append("Type", typeValue);
			if (submissionTag) {
				formData.append("SubmissionTag", submissionTag);
			}

			// For pride posts, add prideType and Description
			if (postType === "pride" && prideType) {
				formData.append("prideType", prideType);
				// Description is already set as content parameter, but ensure it's sent
				formData.append("Description", content);
				console.log("🏆 Pride post details:", {
					prideType,
					Description: content,
					questID: questID > 0 ? questID : null,
					creatureID: creatureID > 0 ? creatureID : null,
				});

				// Add CreatureID if this is a creature pride post
				if (prideType === "Creature" && creatureID > 0) {
					formData.append("CreatureID", creatureID);
				}
			}

			let type = "text";
			if (mediaFile) {
				type = mediaType;
				formData.append("media", mediaFile);

				// If it's an audio or video recording, send the duration
				if (mediaType === "audio" || mediaType === "video") {
					formData.append("duration", recordingDuration.toString());
				}
			}
			formData.append("mediaType", type);

			const isContestSubmission =
				contestid !== null &&
				contestid !== undefined &&
				Number(contestid) !== 0;

			// Create post from form data
			const response = await newsfeedApi.createPost(formData);

			// Fire-and-forget: grade in background so UI resolves immediately after createPost.
			// autoJudge=true (contestid=0): auto-approve if score >= 1.
			// autoJudge=false (contestid!=0): AI fills suggestion fields for human judges only.
			if (typeValue === "glow" && response.success && response.result?.ID) {
				newsfeedApi
					.gradeExistingPost(response.result.ID, !isContestSubmission)
					.catch((err) => {
						console.warn("[gradeExistingPost] background grading failed:", err);
					});
			}

			// Update posts state if post created successfully
			if (response.success) {
				const state = get();

				// Deduct 2 lumens for grateful posts
				if (typeValue === "grateful") {
					const currentLumens = userState.user?.CurrentLumens || 0;
					const newLumens = Math.max(0, currentLumens - 2);
					userState.updateUser({ CurrentLumens: newLumens });
					console.log(
						`Deducted 2 lumens for grateful post. New balance: ${newLumens}`
					);
				}

				// Immediately show grateful and pride posts on the newsfeed
				if (typeValue === "grateful" || typeValue === "pride") {
					console.log("Response for grateful/pride post:", response);
					console.log("Response.result:", response.result);
					console.log("Response.result.Glow:", response.result?.Glow);

					// Format the new post data to match the expected structure
					let newPost = null;
					if (response.result?.Glow && response.result.Glow.length > 0) {
						newPost = response.result.Glow[0];
					} else if (response.result) {
						newPost = response.result;
					}

					// Ensure the post has all required fields
					if (newPost) {
						// For pride posts, prioritize PrideID; for grateful, prioritize GratitudeID; otherwise use GlowID
						const postId =
							typeValue === "pride"
								? newPost.PrideID ||
									newPost.GlowID ||
									newPost.ID ||
									newPost.ScoreID
								: typeValue === "grateful"
									? newPost.GratitudeID ||
										newPost.GlowID ||
										newPost.ID ||
										newPost.ScoreID
									: newPost.GlowID ||
										newPost.GratitudeID ||
										newPost.PrideID ||
										newPost.ID ||
										newPost.ScoreID;

						// For pride posts, set MediaURL optimistically if we have a media file
						let optimisticMediaURL = newPost.MediaURL || null;
						if (typeValue === "pride" && mediaFile && mediaType === "image") {
							// Create a preview URL for the uploaded file
							optimisticMediaURL = URL.createObjectURL(mediaFile);
						}

						// Build base post object without ID fields first
						const basePost = {
							Title: newPost.Title || title,
							Glow: newPost.Glow || newPost.Action || content,
							Description: newPost.Description || content,
							CreatedAt:
								newPost.CreatedAt ||
								newPost.Datetime ||
								new Date().toISOString(),
							AuthorName:
								newPost.AuthorName || userState.user?.Username || "You",
							AvatarURL:
								newPost.AvatarURL ||
								userState.user?.AvatarURL ||
								userState.user?.MediaURL ||
								userState.avatar,
							MemberID: newPost.MemberID || userState.user?.MemberID,
							Type: newPost.Type || typeValue,
							Lumens: newPost.Lumens || 0,
							LikesCount: newPost.LikesCount || 0,
							CommentsCount: newPost.CommentsCount || 0,
							SharesCount: newPost.SharesCount || 0,
							IsLiked: false,
							MediaURL: optimisticMediaURL,
							Format: newPost.Format || null,
							ContestID: newPost.ContestID || contestid,
						};

						// Set ONLY the correct ID field based on post type (don't include other ID fields)
						const formattedPost =
							typeValue === "grateful"
								? { ...basePost, GratitudeID: newPost.GratitudeID || postId }
								: typeValue === "pride"
									? { ...basePost, PrideID: newPost.PrideID || postId }
									: { ...basePost, GlowID: newPost.GlowID || postId };

						set({ posts: [formattedPost, ...state.posts] });
						console.log("Added formatted post to newsfeed:", formattedPost);
					}
					if (successToastShown === true) {
						showSuccessToast("Your post has been shared!");
					}
				} else if (successToastShown === true) {
					showSuccessToast("Your Glow is being reviewed!");
				}

				return {
					success: true,
					response,
				};
			}
			// Handle error response
			const errorMessage = response.data?.message || "Unknown error";
			showErrorToast("Failed to create post", errorMessage);
			return { success: false, error: errorMessage };
		} catch (error) {
			console.error("Error creating post:", error);
			const errorMessage =
				error.response?.data?.detail ||
				error.message ||
				"Unknown error occurred";
			showErrorToast("Failed to create post", errorMessage);
			return { success: false, error: errorMessage };
		} finally {
			set({ isPosting: false });
		}
	},

	// Adds new comment to post with GlowID
	handleAddComment: async (
		GlowID,
		Comment,
		ParentID = 0,
		TargetReplyID = 0,
		Format = "Text",
		providedPostType = null
	) => {
		const state = get();
		const userState = useUserStore.getState();
		if (!Comment.trim()) {
			return;
		}

		// Find the post - it could be in GlowID, GratitudeID, or PrideID
		let post = state.posts.find((p) => p.GlowID === GlowID);
		if (!post) {
			post = state.posts.find((p) => p.GratitudeID === GlowID);
		}
		if (!post) {
			post = state.posts.find((p) => p.PrideID === GlowID);
		}

		// Determine post type - use provided type if post not found in store (e.g., on individual post page)
		const postType = providedPostType || (post ? detectPostType(post) : "glow");
		const originalCommentsCount = post?.CommentsCount || 0;

		console.log("📝 Adding comment:", {
			GlowID,
			postType,
			providedPostType,
			foundPost: !!post,
		});

		try {
			// Optimistic UI update - increment comment count immediately
			set((state) => ({
				...state,
				posts: state.posts.map((p) => {
					const matchesId =
						p.GlowID === GlowID ||
						p.GratitudeID === GlowID ||
						p.PrideID === GlowID;
					if (matchesId) {
						return {
							...p,
							CommentsCount: (p.CommentsCount || 0) + 1,
						};
					}
					return p;
				}),
			}));

			// Create comment from data
			const response = await newsfeedApi.addComment(
				postType,
				GlowID,
				Comment,
				userState.user.MemberID,
				TargetReplyID,
				ParentID,
				0, // ClientID
				post?.ContestID || 0, // ContestID
				Format,
				"", // CommentType
				"" // MediaURL
			);

			// If server request is successful, update UI with actual comment
			if (response.data?.success) {
				// If is not reply, add to comments state
				if (ParentID === 0) {
					set({
						comments: {
							...state.comments,
							[GlowID]: [
								...(state.comments[GlowID] || []),
								response.data.result.comment,
							],
						},
					});
				} else {
					// If is reply, add to replies of parent comment
					const commentsCopy = structuredClone(state.comments);
					commentsCopy[GlowID].forEach((comment) => {
						if (comment.CommentID !== ParentID) {
							let found = false;
							comment.Replies?.forEach((reply) => {
								if (reply.CommentID === ParentID) {
									found = true;
								}
							});

							if (!found) {
								return;
							}
						}

						if (!comment.Replies) {
							comment.Replies = [];
						}
						comment.Replies.push(response.data.result.comment);
					});
					set({ comments: commentsCopy });
				}

				showSuccessToast("Comment added successfully!");
			} else {
				// Revert comment count on failure
				set((state) => ({
					...state,
					posts: state.posts.map((p) => {
						const matchesId =
							p.GlowID === GlowID ||
							p.GratitudeID === GlowID ||
							p.PrideID === GlowID;
						if (matchesId) {
							return {
								...p,
								CommentsCount: originalCommentsCount,
							};
						}
						return p;
					}),
				}));

				showErrorToast(
					"Failed to add comment",
					response.data?.error || "Unknown error occurred"
				);
			}
		} catch (error) {
			console.error("Error adding comment:", error);

			// Revert comment count on error
			set((state) => ({
				...state,
				posts: state.posts.map((p) => {
					const matchesId =
						p.GlowID === GlowID ||
						p.GratitudeID === GlowID ||
						p.PrideID === GlowID;
					if (matchesId) {
						return {
							...p,
							CommentsCount: originalCommentsCount,
						};
					}
					return p;
				}),
			}));

			showErrorToast(
				"Failed to add comment",
				error.response?.data?.detail ||
					error.message ||
					"Unknown error occurred"
			);
		}
	},

	// Add like to comment with commentId
	handleLikeComment: async (commentId, GlowID) => {
		const state = get();
		const userState = useUserStore.getState();
		const isLiked = state.likedComments.indexOf(commentId) !== -1;

		try {
			// Optimistic UI update - update likedComments array
			const newLikedComments = [...state.likedComments];
			if (isLiked) {
				newLikedComments.splice(newLikedComments.indexOf(commentId), 1);
			} else {
				newLikedComments.push(commentId);
			}
			set({ likedComments: newLikedComments });

			// Optimistic UI update - update comment likes in the comments state
			const commentsCopy = structuredClone(state.comments);
			if (commentsCopy[GlowID]) {
				commentsCopy[GlowID].forEach((comment) => {
					// If comment is the one being liked, update like
					if (comment.CommentID === commentId) {
						if (isLiked) {
							// Remove like
							comment.Likes =
								comment.Likes?.filter((id) => id !== userState.user.MemberID) ||
								[];
						} else {
							// Add like
							comment.Likes = [
								...(comment.Likes || []),
								userState.user.MemberID,
							];
						}
						comment.IsLiked = !isLiked;
					} else {
						// Check replies if comment is not the one being liked
						comment.Replies?.forEach((reply) => {
							if (reply.CommentID === commentId) {
								if (isLiked) {
									// Remove like
									reply.Likes =
										reply.Likes?.filter(
											(id) => id !== userState.user.MemberID
										) || [];
								} else {
									// Add like
									reply.Likes = [
										...(reply.Likes || []),
										userState.user.MemberID,
									];
								}
								reply.IsLiked = !isLiked;
							}
						});
					}
				});
				set({ comments: commentsCopy });
			}

			// Send request to server
			const response = await newsfeedApi.likeComment(
				commentId,
				userState.user.MemberID
			);

			// Check response - be flexible with response structure
			const isSuccess = response?.data?.success || response?.success;

			if (!isSuccess) {
				console.warn("Comment like response:", response);
				// Revert optimistic updates on failure
				const revertedLikedComments = [...state.likedComments];
				if (!isLiked) {
					revertedLikedComments.splice(
						revertedLikedComments.indexOf(commentId),
						1
					);
				} else {
					revertedLikedComments.push(commentId);
				}
				set({ likedComments: revertedLikedComments });

				// Revert comment likes in state
				const revertedCommentsCopy = structuredClone(state.comments);
				if (revertedCommentsCopy[GlowID]) {
					revertedCommentsCopy[GlowID].forEach((comment) => {
						if (comment.CommentID === commentId) {
							if (!isLiked) {
								comment.Likes =
									comment.Likes?.filter(
										(id) => id !== userState.user.MemberID
									) || [];
							} else {
								comment.Likes = [
									...(comment.Likes || []),
									userState.user.MemberID,
								];
							}
							comment.IsLiked = isLiked;
						} else {
							comment.Replies?.forEach((reply) => {
								if (reply.CommentID === commentId) {
									if (!isLiked) {
										reply.Likes =
											reply.Likes?.filter(
												(id) => id !== userState.user.MemberID
											) || [];
									} else {
										reply.Likes = [
											...(reply.Likes || []),
											userState.user.MemberID,
										];
									}
									reply.IsLiked = isLiked;
								}
							});
						}
					});
					set({ comments: revertedCommentsCopy });
				}

				showErrorToast(
					"Failed to update comment like",
					response.data?.error || response.error || "Unknown error occurred"
				);
			} else {
				// Success!
				showSuccessToast(isLiked ? "Comment unliked" : "Comment liked!");
			}
		} catch (error) {
			console.error("Error liking comment:", error);
			// Revert optimistic updates on error
			const revertedLikedComments = [...state.likedComments];
			if (!isLiked) {
				revertedLikedComments.splice(
					revertedLikedComments.indexOf(commentId),
					1
				);
			} else {
				revertedLikedComments.push(commentId);
			}
			set({ likedComments: revertedLikedComments });

			// Revert comment likes in state
			const revertedCommentsCopy = structuredClone(state.comments);
			if (revertedCommentsCopy[GlowID]) {
				revertedCommentsCopy[GlowID].forEach((comment) => {
					if (comment.CommentID === commentId) {
						if (!isLiked) {
							comment.Likes =
								comment.Likes?.filter((id) => id !== userState.user.MemberID) ||
								[];
						} else {
							comment.Likes = [
								...(comment.Likes || []),
								userState.user.MemberID,
							];
						}
						comment.IsLiked = isLiked;
					} else {
						comment.Replies?.forEach((reply) => {
							if (reply.CommentID === commentId) {
								if (!isLiked) {
									reply.Likes =
										reply.Likes?.filter(
											(id) => id !== userState.user.MemberID
										) || [];
								} else {
									reply.Likes = [
										...(reply.Likes || []),
										userState.user.MemberID,
									];
								}
								reply.IsLiked = isLiked;
							}
						});
					}
				});
				set({ comments: revertedCommentsCopy });
			}

			showErrorToast(
				"Failed to update comment like",
				error.response?.data?.detail ||
					error.message ||
					"Unknown error occurred"
			);
		}
	},

	// Pin/Unpin comment with commentId
	handlePinComment: async (comment, GlowID) => {
		const state = get();
		const userState = useUserStore.getState();
		try {
			// Find the post - it could have GlowID, GratitudeID, or PrideID
			let post = state.posts.find((item) => item.GlowID === GlowID);
			if (!post) {
				post = state.posts.find((item) => item.GratitudeID === GlowID);
			}
			if (!post) {
				post = state.posts.find((item) => item.PrideID === GlowID);
			}

			const postType = detectPostType(post);

			// Check if the comment is already pinned
			const isPinned =
				state.pinnedComments[GlowID]?.CommentID === comment.CommentID;

			// Optimistic UI update
			const newPinnedComments = structuredClone(state.pinnedComments);
			if (isPinned) {
				newPinnedComments[GlowID] = null;
			} else {
				newPinnedComments[GlowID] = structuredClone(comment);
				newPinnedComments[GlowID].IsPinned = true;
			}
			set({ pinnedComments: newPinnedComments });

			// Call API to update the pin status on the server
			try {
				const response = await newsfeedApi.pinComment(
					GlowID,
					comment.CommentID,
					userState.user.MemberID,
					postType
				);

				// If the server request fails, revert the optimistic update
				if (!response.data || !response.data.success) {
					const revertedPinnedComments = structuredClone(state.pinnedComments);
					if (isPinned) {
						revertedPinnedComments[GlowID] = null;
					} else {
						revertedPinnedComments[GlowID] = structuredClone(comment);
						revertedPinnedComments[GlowID].IsPinned = true;
					}
					set({ pinnedComments: revertedPinnedComments });

					showErrorToast(
						"Failed to update pin status",
						response.data?.error || "Unknown error occurred"
					);
				} else {
					const commentsCopy = structuredClone(state.comments);
					commentsCopy[GlowID].forEach((c) => {
						if (c.CommentID === comment.CommentID) {
							c.IsPinned = !isPinned;
						} else {
							c.IsPinned = false;
						}
					});
					set({ comments: commentsCopy });

					showSuccessToast(isPinned ? "Comment unpinned" : "Comment pinned!");
				}
			} catch (error) {
				console.error("Error updating pin status:", error);
				// Revert optimistic update on error
				const revertedPinnedComments = structuredClone(state.pinnedComments);
				if (isPinned) {
					revertedPinnedComments[GlowID] = null;
				} else {
					revertedPinnedComments[GlowID] = structuredClone(comment);
					revertedPinnedComments[GlowID].IsPinned = true;
				}
				set({ pinnedComments: revertedPinnedComments });

				showErrorToast(
					"Failed to update pin status",
					error.response?.data?.detail ||
						error.message ||
						"Unknown error occurred"
				);
			}
		} catch (error) {
			console.error("Error handling pin comment:", error);
			showErrorToast(
				"Failed to update pin status",
				error.response?.data?.detail ||
					error.message ||
					"Unknown error occurred"
			);
		}
	},

	// Share post with itemId
	handleShare: async (itemId, title, skipShare = false) => {
		let shareSuccess = false;
		const state = get();

		// Try to use the Web Share API if available and not skipped
		if (!skipShare && navigator.share) {
			try {
				await navigator.share({
					title,
					text: `Check out this news: ${title}`,
					url: window.location.href,
				});
				shareSuccess = true;
			} catch (error) {
				console.error("Error sharing:", error);
			}
		} else {
			// If skipped or Web Share API not available, mark as success for tracking
			shareSuccess = true;
		}

		// If sharing was successful, update server and UI
		if (shareSuccess) {
			try {
				// Find the post - it could have GlowID, GratitudeID, or PrideID
				let postToShare = state.posts.find((item) => item.GlowID === itemId);
				if (!postToShare) {
					postToShare = state.posts.find((item) => item.GratitudeID === itemId);
				}
				if (!postToShare) {
					postToShare = state.posts.find((item) => item.PrideID === itemId);
				}

				// Inform the server about the share
				const response = await newsfeedApi.sharePost(
					itemId,
					detectPostType(postToShare)
				);

				// If server request is successful, update UI
				if (response.data && response.data.success) {
					const postsCopy = structuredClone(state.posts);
					postsCopy.forEach((item) => {
						// Check all possible ID fields
						if (
							item.GlowID === itemId ||
							item.GratitudeID === itemId ||
							item.PrideID === itemId
						) {
							item.SharesCount = item.SharesCount + 1;
						}
					});

					// Also update selectedPost if it matches the shared post
					let updatedSelectedPost = state.selectedPost;
					if (updatedSelectedPost) {
						const selectedPostId = getPostID(updatedSelectedPost);
						if (selectedPostId === itemId) {
							updatedSelectedPost = {
								...updatedSelectedPost,
								SharesCount: updatedSelectedPost.SharesCount + 1,
							};
						}
					}

					set({ posts: postsCopy, selectedPost: updatedSelectedPost });

					showSuccessToast("Post shared successfully!");
				} else {
					showErrorToast(
						"Failed to update share count",
						response.data?.error || "Unknown error occurred"
					);
				}
			} catch (error) {
				console.error("Error updating share count:", error);
				showErrorToast(
					"Failed to update share count",
					error.response?.data?.detail ||
						error.message ||
						"Unknown error occurred"
				);
			}
		}
	},

	// Delete post with id
	handleDelete: async (id) => {
		const state = get();
		if (state.loadingPosts) {
			return;
		}

		// Find the item to delete - it could have GlowID, GratitudeID, or PrideID
		let itemToDelete = state.posts.find((item) => item.GlowID === id);
		if (!itemToDelete) {
			itemToDelete = state.posts.find((item) => item.GratitudeID === id);
		}
		if (!itemToDelete) {
			itemToDelete = state.posts.find((item) => item.PrideID === id);
		}

		if (!itemToDelete) {
			return;
		}

		// Save the position of the item in the array for restoration if needed
		const itemIndex = state.posts.indexOf(itemToDelete);

		// Optimistic UI update - remove item immediately
		const newItems = state.posts.filter((item) => {
			return (
				item.GlowID !== id && item.GratitudeID !== id && item.PrideID !== id
			);
		});
		set({ posts: newItems });

		try {
			// Call API to delete the post
			const response = await newsfeedApi.deletePost(
				id,
				detectPostType(itemToDelete)
			);

			// Check server response
			if (!response.data || !response.data.success) {
				// Server request failed, revert optimistic update
				console.error("Server failed to delete the post");

				// Restore the item at its original position
				const restoredItems = [...newItems];
				restoredItems.splice(itemIndex, 0, itemToDelete);
				set({ posts: restoredItems });

				// Show error toast
				showErrorToast(
					"Failed to delete post",
					response.data?.error || "Unknown error occurred"
				);
			} else {
				// Show success toast
				showSuccessToast("Post deleted successfully!");
			}
		} catch (error) {
			// Handle network or other errors
			console.error("Error deleting post:", error);

			// Restore the item at its original position
			const restoredItems = [...newItems];
			restoredItems.splice(itemIndex, 0, itemToDelete);
			set({ posts: restoredItems });

			// Show error toast
			showErrorToast(
				"Failed to delete post",
				error.response?.data?.detail ||
					error.message ||
					"Unknown error occurred"
			);
		}
	},

	// Handle sending lumens to a post
	handleSendLumens: async (itemId, lumensToSend, recipientMemberID) => {
		const userState = useUserStore.getState();
		const state = get();

		if (state.loadingPosts) {
			return { success: false, message: "Already loading" };
		}

		const currentUserLumens = userState.user?.CurrentLumens || 0;

		// Validation
		if (lumensToSend < 1) {
			return { success: false, message: "Must send at least 1 lumen" };
		}

		if (lumensToSend > currentUserLumens) {
			return { success: false, message: "Not enough lumens" };
		}

		// Store original post lumens for potential revert
		let originalPost = state.posts.find((item) => item.GlowID === itemId);
		if (!originalPost) {
			originalPost = state.posts.find((item) => item.GratitudeID === itemId);
		}
		if (!originalPost) {
			originalPost = state.posts.find((item) => item.PrideID === itemId);
		}

		if (!originalPost) {
			return { success: false, message: "Post not found" };
		}

		const originalPostLumens = originalPost.Lumens || 0;
		const originalUserLumens = currentUserLumens;

		try {
			// Optimistic UI update - update post lumens and user lumens
			set((state) => ({
				...state,
				posts: state.posts.map((item) => {
					const matchesId =
						item.GlowID === itemId ||
						item.GratitudeID === itemId ||
						item.PrideID === itemId;
					if (matchesId) {
						return {
							...item,
							Lumens: (item.Lumens || 0) + lumensToSend,
						};
					}
					return item;
				}),
			}));

			// Update user's lumens
			userState.updateUser({ CurrentLumens: currentUserLumens - lumensToSend });

			// Send request to server
			const response = await newsfeedApi.sendLumens(
				userState.user.MemberID,
				lumensToSend,
				itemId,
				detectPostType(originalPost)
			);

			if (!response.data || !response.data.success) {
				// Revert optimistic updates if server request fails
				set((state) => ({
					...state,
					posts: state.posts.map((item) => {
						if (item.GlowID === itemId) {
							return {
								...item,
								Lumens: originalPostLumens,
							};
						}
						return item;
					}),
				}));

				userState.updateUser({ CurrentLumens: originalUserLumens });

				const errorMsg =
					response.data?.message ||
					response.data?.error ||
					"Failed to send lumens";
				showErrorToast("Failed to send lumens", errorMsg);
				return { success: false, message: errorMsg };
			}
			// Success!
			showSuccessToast(`Successfully sent ${lumensToSend} lumens! 🔥`);
			return { success: true, message: "Lumens sent successfully" };
		} catch (error) {
			console.error("Error sending lumens:", error);

			// Revert optimistic updates on error
			set((state) => ({
				...state,
				posts: state.posts.map((item) => {
					if (item.GlowID === itemId) {
						return {
							...item,
							Lumens: originalPostLumens,
						};
					}
					return item;
				}),
			}));

			userState.updateUser({ CurrentLumens: originalUserLumens });

			const errorMsg =
				error.response?.data?.message ||
				error.message ||
				"Unknown error occurred";
			showErrorToast("Failed to send lumens", errorMsg);
			return { success: false, message: errorMsg };
		}
	},

	// Get my posts for page number
	getMyPosts: async (pageNum, pageSize = 10) => {
		const state = get();
		const userState = useUserStore.getState();
		if (state.loadingMyPosts) {
			return;
		}

		try {
			set({ loadingMyPosts: true });
			const response = await newsfeedApi.getMyPosts(
				pageNum,
				pageSize,
				userState.user?.MemberID
			);

			// If posts found
			if (response.result?.items) {
				const newItems = response.result.items;

				// Update liked items based on server data
				const likedItems = [...state.likedItems];
				newItems.forEach((item) => {
					if (item.IsLiked) {
						// Get the correct ID based on post type
						const itemId = getPostID(item);
						if (itemId && !likedItems.includes(itemId)) {
							likedItems.push(itemId);
						}
					}
				});
				set({ likedItems });

				if (pageNum === 1) {
					// Replace items for first page
					set({ myPosts: newItems });
				} else {
					// Append for subsequent pages
					set({ myPosts: [...state.myPosts, ...newItems] });
				}

				set({
					hasMoreMyPosts: response.result.hasMore,
					myPostsPageNum: pageNum,
				});
			} else {
				set({ hasMoreMyPosts: false });
			}
			set({ loadingMyPosts: false });
		} catch (error) {
			set({ loadingMyPosts: false });
			console.error("Error loading my posts:", error);
			set({ hasMoreMyPosts: false });
			showErrorToast(
				"Failed to load your posts",
				error.response?.data?.detail ||
					error.message ||
					"Unknown error occurred"
			);
		}
	},

	// Get my sent star salutes for page number
	getMyStarSalutes: async (pageNum, pageSize = 10) => {
		const state = get();
		const userState = useUserStore.getState();
		if (state.loadingMyStarSalutes) {
			return;
		}

		// Check if this page has already been loaded
		if (state.myStarSalutesLoadedPages.includes(pageNum)) {
			console.log(
				`Star salutes page ${pageNum} already loaded, skipping fetch`
			);
			return;
		}

		try {
			set({ loadingMyStarSalutes: true });
			const response = await userDashboardAPI.getMySentStarSalutes(
				userState.user?.MemberID,
				pageNum,
				pageSize
			);

			console.log("Star salutes response:", response);

			// If salutes found - check for result.salutes instead of result.items
			if (response.result?.salutes) {
				const newItems = response.result.salutes;

				if (pageNum === 1 && state.myStarSalutes.length === 0) {
					// Only replace if array is empty (first load)
					set({
						myStarSalutes: newItems,
						myStarSalutesLoadedPages: [pageNum],
					});
				} else {
					// Append for subsequent pages or if data already exists
					const updatedPages = [...state.myStarSalutesLoadedPages];
					if (!updatedPages.includes(pageNum)) {
						updatedPages.push(pageNum);
					}
					set({
						myStarSalutes: [...state.myStarSalutes, ...newItems],
						myStarSalutesLoadedPages: updatedPages,
					});
				}

				set({
					hasMoreMyStarSalutes: response.result.hasMore || false,
					myStarSalutesPageNum: Math.max(state.myStarSalutesPageNum, pageNum),
				});
			} else {
				set({ hasMoreMyStarSalutes: false });
			}
			set({ loadingMyStarSalutes: false });
		} catch (error) {
			set({ loadingMyStarSalutes: false });
			console.error("Error loading star salutes:", error);
			set({ hasMoreMyStarSalutes: false });
			showErrorToast(
				"Failed to load star salutes",
				error.response?.data?.detail ||
					error.message ||
					"Unknown error occurred"
			);
		}
	},

	// Get my sent secret salutes for page number
	getMySecretSalutes: async (pageNum, pageSize = 10) => {
		const state = get();
		const userState = useUserStore.getState();
		if (state.loadingMySecretSalutes) {
			return;
		}

		// Check if this page has already been loaded
		if (state.mySecretSalutesLoadedPages.includes(pageNum)) {
			console.log(
				`Secret salutes page ${pageNum} already loaded, skipping fetch`
			);
			return;
		}

		try {
			set({ loadingMySecretSalutes: true });
			const response = await userDashboardAPI.getMySentSecretSalutes(
				userState.user?.MemberID,
				pageNum,
				pageSize
			);

			console.log("Secret salutes response:", response);

			// If salutes found - check for result.salutes instead of result.items
			if (response.result?.salutes) {
				const newItems = response.result.salutes;

				if (pageNum === 1 && state.mySecretSalutes.length === 0) {
					// Only replace if array is empty (first load)
					set({
						mySecretSalutes: newItems,
						mySecretSalutesLoadedPages: [pageNum],
					});
				} else {
					// Append for subsequent pages or if data already exists
					const updatedPages = [...state.mySecretSalutesLoadedPages];
					if (!updatedPages.includes(pageNum)) {
						updatedPages.push(pageNum);
					}
					set({
						mySecretSalutes: [...state.mySecretSalutes, ...newItems],
						mySecretSalutesLoadedPages: updatedPages,
					});
				}

				set({
					hasMoreMySecretSalutes: response.result.hasMore || false,
					mySecretSalutesPageNum: Math.max(
						state.mySecretSalutesPageNum,
						pageNum
					),
				});
			} else {
				set({ hasMoreMySecretSalutes: false });
			}
			set({ loadingMySecretSalutes: false });
		} catch (error) {
			set({ loadingMySecretSalutes: false });
			console.error("Error loading secret salutes:", error);
			set({ hasMoreMySecretSalutes: false });
			showErrorToast(
				"Failed to load secret salutes",
				error.response?.data?.detail ||
					error.message ||
					"Unknown error occurred"
			);
		}
	},

	// Set the currently selected post for single post view
	setSelectedPost: (post) => {
		set({ selectedPost: post });
	},
}));

export default useNewsfeed;
